/**
 * Configuration specific to the grouped Floating Action Burst and its
 * orchestration in `FloatingMenu`.
 *
 * Tunable layout for arc / line expansion (margins, radii, spacing) lives
 * here alongside long-press, safe-areas and menu sync. Pure geometry math
 * stays in `floatingActionBurstGeometry.ts`, which imports these values.
 * Physical diameters shared with the GPS FAB stay in
 * `../floatingAction.configuration.ts` so both visible primary FABs
 * still read a single source of truth for pixel-perfect alignment.
 */

import { SUB_FAB_SIZE_PX } from "../floatingAction.configuration";

/**
 * Outer margin (px) applied around each sub-FAB wrapper (Tailwind `m-1`).
 * Used by burst geometry to reserve clear space between neighbours on an
 * arc layout and by `SubFloatingAction` styling.
 */
export const SUB_FAB_OUTER_MARGIN_PX = 4;

/**
 * Half-box (px) used by the burst geometry when building the angular
 * clearance mask around the main FAB. Equal to half of a sub-FAB plus
 * the outer margin so the mask accounts for the full footprint of each
 * item.
 */
export const SUB_FAB_HALF_BOX_PX = SUB_FAB_SIZE_PX / 2 + SUB_FAB_OUTER_MARGIN_PX;

/**
 * Vertical gap (px) between the bottom edge of the search bars and the
 * top of the FAB row. Provides visual breathing room while keeping the
 * FABs attached to the bars when merge mode toggles.
 */
export const SEARCH_BARS_VERTICAL_GAP_PX = 10;

/**
 * Fallback top offset (px) used before the search fields are mounted and
 * measurable. Matches the previous static `top-40` Tailwind layout so
 * there is no visual jump on initial paint. Also passed to the burst as
 * default top safe-area when metrics are missing.
 */
export const FALLBACK_TOP_PX = 160;

/** DOM id of the GPS / Dynamic Island / burst FAB row in {@link FloatingMenu}. */
export const FLOATING_MENU_FAB_ROW_ELEMENT_ID = "FloatingMenuFabRow";

/** DOM id of the floating menu layer that tracks search-bar vertical motion. */
export const FLOATING_MENU_LAYER_ELEMENT_ID = "FloatingMenuLayer";

/**
 * Default bottom safe-area (px) reserved for the arc/line expansion when
 * the ItineraryPanel height is unknown. Prevents the burst items from touching
 * the area where the panel usually sits.
 */
export const DEFAULT_SAFE_MARGIN_BOTTOM_PX = 120;

/**
 * Extra gap (px) reserved above the ItineraryPanel so burst items never touch
 * its top edge when the panel is open.
 */
export const ITINERARY_PANEL_VERTICAL_GAP_PX = 12;

/**
 * Duration (ms) of the one-shot discoverability auto-open. After the
 * burst is shown for the first time it closes automatically once this
 * delay elapses so it does not linger in the user's way.
 */
export const AUTO_OPEN_DURATION_MS = 5000;

/**
 * Long-press threshold (ms) above which a pointer-down on the main FAB
 * switches the burst expansion mode. Also the point where the burst
 * is opened automatically so the user can immediately see the new
 * layout.
 */
export const DEFAULT_LONG_PRESS_MS = 500;

/**
 * Tolerance (px) allowed during a pending long-press. Any pointer
 * movement beyond this radius cancels the long-press so users who scroll
 * or drag from the main FAB do not accidentally switch mode.
 */
export const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

/**
 * Edge margin (px) applied on every side when building the angular mask.
 * Prevents items from sitting flush against a viewport edge.
 */
export const VIEWPORT_EDGE_MARGIN = 10;

/** Minimum arc sweep (deg) considered usable before falling back to line. */
export const MIN_ARC_LENGTH_DEG = 30;

/** Maximum arc sweep (deg) before we clamp and orient the opening manually. */
export const MAX_ARC_LENGTH_DEG = 300;

/** Minimum per-item angular density (deg) required to keep arcs readable. */
export const MIN_ITEM_ANGULAR_DENSITY_DEG = 20;

/** Padding (deg) trimmed from both ends of a wide arc for aesthetics. */
export const ARC_ENDPOINT_INSET_DEG = 15;

/** Arc length (deg) under which the endpoint inset is NOT applied. */
export const ARC_ENDPOINT_INSET_THRESHOLD_DEG = 45;

/** Minimum arc radius (px) accepted during progressive radius tuning. */
export const ARC_MIN_RADIUS_PX = 64;

/** Maximum arc radius (px) accepted during progressive radius tuning. */
export const ARC_MAX_RADIUS_PX = 220;

/** Shrink step (px) applied when searching for a fitting arc radius. */
export const ARC_RADIUS_SHRINK_STEP_PX = 12;

/** Growth step (px) applied when searching for a fitting arc radius. */
export const ARC_RADIUS_GROW_STEP_PX = 20;

/** Default pixel spacing between successive items in line mode. */
export const DEFAULT_LINE_SPACING_PX = 60;

/** Minimal item spacing (px) accepted when line layout is compressed. */
export const MIN_LINE_SPACING_PX = 44;

/**
 * When `true`, the burst always deploys as a vertical line downward and
 * ignores arc layout and auto direction selection. Arc geometry, long-press
 * mode toggle wiring and persisted `fabExpansionMode` stay in place so the
 * feature can be re-enabled by flipping this flag to `false`.
 */
export const FAB_BURST_LOCK_VERTICAL_DOWN = true;
